import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle2, Clock, Wifi, WifiOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  complete: { bg: '#064e3b', border: '#10b981', text: '#a7f3d0', label: 'COMPLETE' },
  in_progress: { bg: '#1e3a8a', border: '#3b82f6', text: '#bfdbfe', label: 'IN PROGRESS' },
  no_activity: { bg: '#7c2d12', border: '#f59e0b', text: '#fde68a', label: 'NO ACTIVITY' },
  no_expected: { bg: '#1f2937', border: '#4b5563', text: '#9ca3af', label: 'NO RIDERS' },
};

function getTodayEST() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

export default function LiveAttendanceDashboard() {
  const [date, setDate] = useState(getTodayEST());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const [error, setError] = useState(null);
  const [paused, setPaused] = useState(false);
  const pollTimerRef = useRef(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 20000);
      const r = await fetch(`${API_URL}/api/admin/attendance-dashboard?date=${date}&_t=${Date.now()}`,
        { cache: 'no-store', signal: controller.signal });
      clearTimeout(tid);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setData(j);
      setLastFetched(new Date());
      setError(null);
    } catch (e) {
      setError(e.message || 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  useEffect(() => {
    if (paused) return;
    const tick = async () => {
      await fetchDashboard();
      pollTimerRef.current = setTimeout(tick, 10000);
    };
    pollTimerRef.current = setTimeout(tick, 10000);
    return () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); };
  }, [paused, fetchDashboard]);

  const t = data?.totals || {};
  const buses = data?.buses || [];
  const unresolved = data?.errors?.unresolved || [];
  const resolved = data?.errors?.resolved_recent || [];

  const headerStats = [
    { label: 'AM Expected', value: t.expected_am || 0, color: '#9ca3af' },
    { label: 'AM Present', value: t.present_am || 0, color: '#10b981' },
    { label: 'AM Absent', value: t.absent_am || 0, color: '#ef4444' },
    { label: 'AM Unmarked', value: (t.expected_am || 0) - (t.present_am || 0) - (t.absent_am || 0), color: '#f59e0b' },
    { label: 'PM Expected', value: t.expected_pm || 0, color: '#9ca3af' },
    { label: 'PM Present', value: t.present_pm || 0, color: '#10b981' },
    { label: 'PM Absent', value: t.absent_pm || 0, color: '#ef4444' },
    { label: 'PM Unmarked', value: (t.expected_pm || 0) - (t.present_pm || 0) - (t.absent_pm || 0), color: '#f59e0b' },
  ];

  return (
    <div data-testid="live-attendance-dashboard" style={{color:'white'}}>
      {/* Date + status bar */}
      <div style={{background:'#1f2937',padding:12,borderRadius:10,marginBottom:12,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
        <input
          data-testid="dashboard-date-input"
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          style={{padding:8,fontSize:14,background:'#111827',color:'white',border:'1px solid #374151',borderRadius:6}}
        />
        <button
          data-testid="dashboard-refresh-btn"
          onClick={fetchDashboard}
          style={{display:'flex',alignItems:'center',gap:6,padding:'8px 12px',background:'#2563eb',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:13,fontWeight:600}}
        >
          <RefreshCw size={14} className={loading ? 'spinner' : ''} /> Refresh
        </button>
        <button
          data-testid="dashboard-pause-btn"
          onClick={() => setPaused(p => !p)}
          style={{display:'flex',alignItems:'center',gap:6,padding:'8px 12px',background:paused?'#f59e0b':'#374151',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:13,fontWeight:600}}
        >
          {paused ? <WifiOff size={14}/> : <Wifi size={14}/>} {paused ? 'Resume' : 'Pause Auto-Refresh'}
        </button>
        <div style={{fontSize:12,color:'#9ca3af',marginLeft:'auto'}}>
          {lastFetched ? `Updated ${lastFetched.toLocaleTimeString('en-US',{timeZone:'America/New_York'})} ET` : '—'}
          {!paused && <span style={{marginLeft:8,color:'#10b981'}}>● LIVE (10s)</span>}
        </div>
      </div>

      {error && (
        <div data-testid="dashboard-fetch-error" style={{background:'#7f1d1d',color:'white',padding:10,borderRadius:8,marginBottom:12,fontSize:13}}>
          ⚠ Could not refresh: {error}. Next attempt in 10 seconds.
        </div>
      )}

      {/* Totals strip */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4, 1fr)',gap:8,marginBottom:12}}>
        {headerStats.map(s => (
          <div key={s.label} style={{background:'#1f2937',padding:10,borderRadius:8,textAlign:'center'}}>
            <div style={{fontSize:11,color:'#9ca3af',marginBottom:4}}>{s.label}</div>
            <div data-testid={`total-${s.label.toLowerCase().replace(' ','-')}`} style={{fontSize:22,fontWeight:700,color:s.color}}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Errors */}
      {(unresolved.length > 0 || resolved.length > 0) && (
        <div data-testid="dashboard-errors" style={{marginBottom:12}}>
          {unresolved.length > 0 && (
            <div style={{background:'#7f1d1d',border:'1px solid #ef4444',padding:10,borderRadius:8,marginBottom:6}}>
              <div style={{display:'flex',alignItems:'center',gap:6,fontWeight:700,marginBottom:6,color:'#fca5a5'}}>
                <AlertTriangle size={14}/> Unresolved errors ({unresolved.length})
              </div>
              <div style={{maxHeight:160,overflowY:'auto'}}>
                {unresolved.slice(0,20).map((e,i) => (
                  <div key={i} data-testid={`unresolved-error-${i}`} style={{fontSize:12,color:'#fecaca',padding:'4px 0',borderBottom:'1px solid #991b1b'}}>
                    <span style={{color:'white',fontWeight:600}}>{e.bus_number}</span>
                    {' '}— {e.camper_id || '(no camper)'} — <em style={{color:'#fca5a5'}}>{e.kind}</em> {e.error ? `: ${e.error}` : ''}
                    <span style={{float:'right',color:'#fda4af'}}>{e.timestamp ? new Date(e.timestamp).toLocaleTimeString('en-US',{timeZone:'America/New_York'}) : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {resolved.length > 0 && (
            <details style={{background:'#064e3b',border:'1px solid #10b981',padding:10,borderRadius:8}}>
              <summary style={{display:'flex',alignItems:'center',gap:6,fontWeight:700,color:'#a7f3d0',cursor:'pointer'}}>
                <CheckCircle2 size={14}/> Resolved errors ({resolved.length}) — click to expand
              </summary>
              <div style={{maxHeight:160,overflowY:'auto',marginTop:6}}>
                {resolved.slice(0,30).map((e,i) => (
                  <div key={i} data-testid={`resolved-error-${i}`} style={{fontSize:12,color:'#bbf7d0',padding:'4px 0',borderBottom:'1px solid #065f46'}}>
                    <span style={{color:'white',fontWeight:600}}>{e.bus_number}</span> — {e.camper_id || '(no camper)'} — <em>{e.kind}</em>
                    <span style={{float:'right',color:'#86efac'}}>resolved {e.resolved_at ? new Date(e.resolved_at).toLocaleTimeString('en-US',{timeZone:'America/New_York'}) : ''}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Bus grid */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))',gap:8}}>
        {buses.map(b => {
          const c = STATUS_COLOR[b.status] || STATUS_COLOR.no_expected;
          return (
            <div key={b.bus_number} data-testid={`bus-card-${b.bus_number.replace(/\W/g,'')}`}
              style={{background:c.bg,border:`2px solid ${c.border}`,borderRadius:10,padding:10}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                <div style={{fontSize:15,fontWeight:700,color:'white'}}>{b.bus_number}</div>
                <span style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:4,background:c.border,color:'white'}}>{c.label}</span>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4, 1fr)',gap:4,fontSize:11}}>
                <div style={{textAlign:'center'}}>
                  <div style={{color:c.text,fontWeight:600,fontSize:9}}>AM EXP</div>
                  <div style={{fontWeight:700}}>{b.expected_am}</div>
                </div>
                <div style={{textAlign:'center'}}>
                  <div style={{color:'#10b981',fontWeight:600,fontSize:9}}>PRES</div>
                  <div style={{fontWeight:700,color:'#a7f3d0'}}>{b.marked_am_present}</div>
                </div>
                <div style={{textAlign:'center'}}>
                  <div style={{color:'#ef4444',fontWeight:600,fontSize:9}}>ABS</div>
                  <div style={{fontWeight:700,color:'#fecaca'}}>{b.marked_am_absent}</div>
                </div>
                <div style={{textAlign:'center'}}>
                  <div style={{color:'#f59e0b',fontWeight:600,fontSize:9}}>OPEN</div>
                  <div style={{fontWeight:700,color:'#fde68a'}}>{b.unmarked_am}</div>
                </div>
              </div>
              <div style={{height:1,background:c.border,opacity:0.4,margin:'6px 0'}}/>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4, 1fr)',gap:4,fontSize:11}}>
                <div style={{textAlign:'center'}}>
                  <div style={{color:c.text,fontWeight:600,fontSize:9}}>PM EXP</div>
                  <div style={{fontWeight:700}}>{b.expected_pm}</div>
                </div>
                <div style={{textAlign:'center'}}>
                  <div style={{color:'#10b981',fontWeight:600,fontSize:9}}>PRES</div>
                  <div style={{fontWeight:700,color:'#a7f3d0'}}>{b.marked_pm_present}</div>
                </div>
                <div style={{textAlign:'center'}}>
                  <div style={{color:'#ef4444',fontWeight:600,fontSize:9}}>ABS</div>
                  <div style={{fontWeight:700,color:'#fecaca'}}>{b.marked_pm_absent}</div>
                </div>
                <div style={{textAlign:'center'}}>
                  <div style={{color:'#f59e0b',fontWeight:600,fontSize:9}}>OPEN</div>
                  <div style={{fontWeight:700,color:'#fde68a'}}>{b.unmarked_pm}</div>
                </div>
              </div>
              {b.minutes_since_last_mark != null && (
                <div style={{marginTop:6,fontSize:10,color:c.text,display:'flex',alignItems:'center',gap:4}}>
                  <Clock size={10}/> Last mark {b.minutes_since_last_mark < 1 ? '<1' : Math.round(b.minutes_since_last_mark)} min ago
                </div>
              )}
            </div>
          );
        })}
      </div>
      <style>{`
        .spinner { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
